import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AuthUser } from '../types';

export type { AuthUser } from '../types';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaPending: boolean;
  tenantDomain: string | null;
}

/**
 * Der Rumpf eines Zugangstokens, ohne Prüfung der Signatur — die macht der
 * Dienst. JWT kodiert ihn als base64url; `atob` allein kennt `-` und `_`
 * nicht und scheitert an manchen Tokens.
 */
function rumpfAusToken(token: string | null | undefined): Record<string, unknown> | null {
  try {
    if (!token) return null;
    const teil = token.split('.')[1] ?? '';
    const base64 = teil.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(teil.length / 4) * 4, '=');
    const rumpf = JSON.parse(atob(base64));
    return rumpf && typeof rumpf === 'object' ? (rumpf as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Wem ein Zugangstoken gehört: Person und Mandant — auch wenn es abgelaufen ist. */
export interface TokenOwner {
  userId: string;
  tenantId: string;
}

export function tokenOwner(token: string | null | undefined): TokenOwner | null {
  const rumpf = rumpfAusToken(token);
  const userId = rumpf?.userId ?? rumpf?.sub;
  if (!rumpf || typeof userId !== 'string' || !userId) return null;
  return { userId, tenantId: typeof rumpf.tenantId === 'string' ? rumpf.tenantId : '' };
}

/**
 * Gehören die beiden Tokens verschiedenen Personen (oder Mandanten)?
 * Ist eines nicht zu lesen, lässt sich nichts sagen — dann `false`.
 */
export function isDifferentOwner(vorher: string | null | undefined, nachher: string | null | undefined): boolean {
  const alt = tokenOwner(vorher);
  const neu = tokenOwner(nachher);
  if (!alt || !neu) return false;
  return alt.userId !== neu.userId || alt.tenantId !== neu.tenantId;
}

/**
 * Der Benutzer aus dem Zugangstoken — für den Seitenaufbau und nach jeder
 * Erneuerung. Ist das Token abgelaufen, gibt es keinen Benutzer; die Token
 * bleiben aber liegen. Sie wegzuwerfen war der Fehler: Das Erneuerungstoken
 * lebt sieben Tage, das Zugangstoken fünfzehn Minuten. Wer nach einer halben
 * Stunde die Seite neu lud, wurde abgemeldet, obwohl seine Sitzung noch gut
 * war — die Oberfläche hatte den Schlüssel selbst weggeworfen, bevor jemand
 * ihn benutzen konnte.
 */
export function benutzerAusToken(token: string | null): AuthUser | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload = rumpfAusToken(token) as any;
    if (!payload) return null;
    if (payload.exp && payload.exp * 1000 < Date.now()) return null;
    return {
      id: payload.userId || '',
      email: payload.email || '',
      firstName: payload.firstName || '',
      lastName: payload.lastName || '',
      roles: payload.roles || [],
      permissions: (payload.permissions || []).filter(Boolean),
      tenantId: payload.tenantId || '',
      tenantName: localStorage.getItem('tenantDomain') || '',
      mfaEnabled: false,
    };
  } catch {
    return null;
  }
}

const restoredUser = benutzerAusToken(localStorage.getItem('accessToken'));

const initialState: AuthState = {
  user: restoredUser,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!restoredUser,
  isLoading: false,
  mfaPending: false,
  tenantDomain: localStorage.getItem('tenantDomain'),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials(
      state,
      action: PayloadAction<{ user: AuthUser; accessToken: string; refreshToken: string }>
    ) {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.mfaPending = false;
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
    },
    setTokens(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
      // Wer erneuert, ist angemeldet. Nach dem Neuladen mit abgelaufenem
      // Zugangstoken steht hier der Benutzer noch nicht — er steht im neuen
      // Token, und ohne ihn bliebe die Seite gesperrt, obwohl sie offen ist.
      const benutzer = benutzerAusToken(action.payload.accessToken);
      if (benutzer) {
        state.user = { ...benutzer, tenantName: state.user?.tenantName || benutzer.tenantName };
        state.isAuthenticated = true;
      }
    },
    setMfaPending(state, action: PayloadAction<boolean>) {
      state.mfaPending = action.payload;
    },
    setTenantDomain(state, action: PayloadAction<string>) {
      state.tenantDomain = action.payload;
      localStorage.setItem('tenantDomain', action.payload);
    },
    logout(state) {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.mfaPending = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    },
  },
  extraReducers: () => {
    // Auth API matchers are handled via setCredentials/logout dispatched from components
    // to avoid circular dependency between authSlice and authApi
  },
});

export const { setCredentials, setTokens, setMfaPending, setTenantDomain, logout } =
  authSlice.actions;
export default authSlice.reducer;
